<template>
  <div>
    <!-- Filters -->
    <projects-list-filters
        :state-filter.sync="stateFilter"
        :state-options="stateOptions"
        :type-filter.sync="typeFilter"
        :type-options="typeOptions"
    />


    <!-- Add New Project Sidebar -->
    <project-list-add-new
        :countries="countryOptions"
        :departments="departmentsOptions"
        :is-add-new-project-sidebar-active.sync="isAddNewProjectSidebarActive"
        :limit="suggestLimit"
        :type-options="typeOptions"
        @on-customer-change="onInputChange"
        @refetch-data="refetchData"
    />

    <!-- Table Container Card -->
    <b-card class="mb-0" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              cols="12"
              md="3"
          >
            <label>Afficher</label>
            <v-select
                v-model="perPage"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                class="per-page-selector d-inline-block mx-50"
            />
            <label>Projets</label>
          </b-col>

          <b-col
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              cols="12"
              md="3"
          >
            <b-button
                v-if="$can('create_project', 'Projets') && !isModalOpened"
                size="sm"
                variant="success"
                @click="openMapModal"
            >
              <span class="text-nowrap">
                Voir Carte
                <feather-icon class="ml-50" icon="MapPinIcon" size="16"/>
              </span>
            </b-button>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                  v-model="locationQuery"
                  class="d-inline-block mr-1"
                  placeholder="Localisation..."
              />

              <b-form-input
                  v-model="customerQuery"
                  class="d-inline-block mr-1"
                  placeholder="Client..."
              />
              <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  placeholder="Rechercher..."
              />
              <b-button
                  v-if="$can('create_project', 'Projets')"
                  variant="primary"
                  @click="isAddNewProjectSidebarActive = true"
              >
                <span class="text-nowrap">Ajouter un avenant</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
          ref="refProjectListTable"
          :fields="tableColumns"
          :items="fetchProjects"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          class="position-relative"
          empty-text="Aucun enregistrements correspondants trouvés"
          hover
          primary-key="id"
          responsive
          show-empty
      >
        <!-- Column: Code -->
        <template #cell(project_code)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                  v-if="!data.item.deleted_at"
                  :src="data.item.avatar"
                  :text="avatarText(data.item.project_code)"
                  :to="{ name: 'projects-view', params: { id: data.item.uid } }"
                  :variant="`light-${resolveProjectTypeVariant('')}`"
                  size="32"
              />
              <b-avatar
                  v-else
                  :src="data.item.avatar"
                  :text="avatarText(data.item.project_code)"
                  :variant="`light-${resolveProjectTypeVariant('')}`"
                  size="32"
              />
            </template>
            <b-link
                v-if="$can('read_project', 'Projets')"
                :to="{ name: 'projects-view', params: { id: data.item.uid } }"
                class="font-weight-bold d-block text-nowrap"
            >
              {{
                data.item.project_code
              }}<sup
                :class="projectStateClass(data.item.inventory).class"
                class="ml-1"
            ><span class="p-1">{{
                projectStateClass(data.item.inventory).text
              }}</span></sup
            >
            </b-link>
            <span v-else class="font-weight-bold d-block text-nowrap">
              {{
                data.item.project_code
              }}<sup
                :class="projectStateClass(data.item.inventory).class"
                class="ml-1"
            ><span class="p-1">{{
                projectStateClass(data.item.inventory).text
              }}</span></sup
            >
            </span>
            <small class="text-muted text-truncate">{{
                data.item.customer.name
              }}</small>
            <br/>
            <div v-if="$can('track_budget', 'Projets')">
              <small class="text-muted text-truncate"><b>HT:</b> {{
                  amountToCurrency(getValue(data.item.inventory, 'total_pretax'))
                }}</small>
              <br/>
              <small class="text-muted text-truncate"><b>Payé:</b> {{
                  amountToCurrency(getValue(data.item.inventory, 'total_received'))
                }}</small>
            </div>
          </b-media>
        </template>

        <!-- Column: Brief -->
        <template #cell(brief)="data">
          <div
              class="text-truncate text-wrap"
              style="max-width: 150px; max-height: 60px"
          >
            <div v-html="data.item.brief"></div>
          </div>
        </template>

        <!-- Column: Type -->
        <template #cell(type)="data">
          <div class="text-nowrap">
            <feather-icon
                :class="`text-${resolveProjectTypeVariant(data.item.type)}`"
                :icon="resolveProjectTypeIcon(data.item.type)"
                class="mr-50"
                size="18"
            />
            <span class="align-text-top text-capitalize">{{
                data.item.type
              }}</span>
          </div>
        </template>

        <!-- Column: Created At -->
        <template #cell(created_at)="data">
          <div class="text-wrap">{{ formatDate(data.item.created_at) }}</div>
        </template>

        <!-- Column: responsible -->
        <template #cell(responsible)="data">
          <span class="align-text-top text-capitalize">{{
              data.item.assignee ? data.item.assignee.name : ""
            }}</span>
        </template>

        <!-- Column: Actions -->
        <template
            v-if="
            $can('read_project', 'Projets') || $can('edit_project', 'Projets')
          "
            #cell(actions)="data"
        >
          <b-dropdown
              v-if="!data.item.deleted_at"
              :right="$store.state.appConfig.isRTL"
              no-caret
              variant="link"
          >
            <template #button-content>
              <feather-icon
                  class="align-middle text-body"
                  icon="MoreVerticalIcon"
                  size="16"
              />
            </template>
            <b-dropdown-item
                v-if="$can('read_project', 'Projets')"
                :to="{ name: 'projects-view', params: { id: data.item.uid } }"
            >
              <feather-icon icon="FileTextIcon"/>
              <span class="align-middle ml-50">Détails</span>
            </b-dropdown-item>

            <b-dropdown-item
                v-if="$can('edit_project', 'Projets')"
                :to="{ name: 'projects-edit', params: { id: data.item.uid } }"
            >
              <feather-icon icon="EditIcon"/>
              <span class="align-middle ml-50">Modifier</span>
            </b-dropdown-item>
          </b-dropdown>
          <b-badge
              v-else
              :variant="`light-danger`"
              class="text-capitalize"
              pill
          >
            <feather-icon icon="XIcon"/>
          </b-badge>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
              cols="12"
              sm="6"
          >
            <span class="text-muted"
            >Affichage de {{ dataMeta.from }} à {{ dataMeta.to }} sur
              {{ dataMeta.of }} Projets</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
              cols="12"
              sm="6"
          >
            <b-pagination
                v-model="currentPage"
                :per-page="perPage"
                :total-rows="totalProjects"
                class="mb-0 mt-1 mt-sm-0"
                first-number
                last-number
                next-class="next-item"
                prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <b-modal
        v-model="isModalOpened"
        ok-only
        ok-title="Fermer"
        scrollable
        size="xl"
        title="Visualisation de carte"
        @hidden="closeMapModal"
    >
      <multiple-maps :markers="positionMarkers"/>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BModal,
  BPagination,
  BRow,
  BTable,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import {onMounted, ref} from "@vue/composition-api";
import {avatarText, formatDate} from "@core/utils/filter";
import ProjectsListFilters from "./ProjectsListFilters.vue";
import useProjectsList from "./useProjectsList";
import ProjectListAddNew from "./ProjectListAddNew.vue";
import MultipleMaps from "@/views/apps/project/maps/MultipleMaps.vue";
import {amountToCurrency} from "@/utils/formatAmount";
import {typeOptions} from "@/constants/bid";

export default {
  name: "ProjectsList",
  methods: {amountToCurrency},
  components: {
    MultipleMaps,
    ProjectsListFilters,
    ProjectListAddNew,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BModal,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
  },
  setup() {
    const isAddNewProjectSidebarActive = ref(false);
    const isModalOpened = ref(false);
    const isLoading = ref(false);
    const positionMarkers = ref([]);

    onMounted(() => {
      let props = ["customers", "departments", "countries", "status"];
      fetchProjectProperties(props);
    });
    const {
      fetchProjects,
      fetchProjectProperties,
      tableColumns,
      perPage,
      currentPage,
      totalProjects,
      dataMeta,
      perPageOptions,
      customerQuery,
      locationQuery,
      searchQuery,
      sortBy,
      suggestLimit,
      isSortDirDesc,
      refProjectListTable,
      onInputChange,
      refetchData,

      // UI
      resolveProjectTypeVariant,
      resolveProjectTypeIcon,
      projectStateClass,

      // Extra Filters
      stateFilter,
      typeFilter,
      stateOptions,
      customersFilter,
      countryOptions,
      departmentsOptions,
    } = useProjectsList();

    const getAllProjectsPosition = async () => {
      const locations = await store.dispatch("project/fetchProjectsLocation");

      if (locations) {
        positionMarkers.value = locations
      }
      isLoading.value = false;
    }

    const openMapModal = async () => {
      isLoading.value = true
      isModalOpened.value = true;
      await getAllProjectsPosition();
    }

    const closeMapModal = () => {
      positionMarkers.value = [];
    }

    const getValue = (data, key) => {
      return data?.[key] ? data[key] : 0;
    }

    return {
      isAddNewProjectSidebarActive,
      openMapModal,
      closeMapModal,
      isModalOpened,
      isLoading,

      fetchProjects,
      tableColumns,
      perPage,
      currentPage,
      totalProjects,
      dataMeta,
      perPageOptions,
      searchQuery,
      customerQuery,
      locationQuery,
      sortBy,
      suggestLimit,
      isSortDirDesc,
      positionMarkers,
      refProjectListTable,
      onInputChange,
      refetchData,
      getValue,

      // Filter
      formatDate,
      avatarText,

      // UI
      resolveProjectTypeVariant,
      resolveProjectTypeIcon,
      projectStateClass,

      stateOptions,
      typeOptions,
      customersFilter,

      // Extra Filters
      stateFilter,
      typeFilter,
      countryOptions,
      departmentsOptions,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
